import React, { FunctionComponent, HTMLAttributes, memo, useCallback } from 'react'
import styles from './HistoryElement.module.sass'
import clsx from 'clsx'
import { HistoryData, HistoryResources, isResultHistory } from '../../../../responses/history'
import { ID } from '../../../../types/UtilityTypes'
import { CommentEditState } from '../../../../redux/global/history/history-reducer'
import { elem } from 'fp-ts/es6/Array'
import { eqId } from '../../../../utils/entity-utils'
import { useRootSelector } from '../../../../utils/hooks'
import { historyRenderer } from '../../../../utils/renderers'
import UserPanel from '../UserElement/UserElement'
import SideMark from '../../../etc/SideMark/SideMark'
import { Colors } from '../../../../styles'
import { userDescription } from '../../../../utils/lenses'
import { EMPTY_USER } from '../../../../responses/users'
import { processDate } from '../../../../utils/date-utils'
import { ADTType } from '@morphic-ts/adt'

const hasActiveEditor = (data: HistoryData, editState: ADTType<typeof CommentEditState>): boolean => {
    const ids = data.actions
        .filter(action => action.key === 'comment')
        .flatMap(action => action.values)
    return CommentEditState.is.update(editState) && elem(eqId)(editState.comment.id, ids as ID[])
}

interface Props extends HTMLAttributes<HTMLDivElement> {
    data: HistoryData,
    resources: HistoryResources,
    onRead: (id: ID) => void,
}

const HistoryElement: FunctionComponent<Props> = (
    {
        data,
        resources,
        onRead,
        className,
        children,
    }
) => {
    const editState = useRootSelector(state => state.global.history.editState)
    const tryRead = useCallback(() => {
        !data.read && onRead(data.id)
    }, [data.read, data.id, onRead])

    return <div
        className={clsx(className, styles.element, {
            [styles.edit]: hasActiveEditor(data, editState)
        })}
        onMouseEnter={tryRead}
    >
        <SideMark color={
            !data.read? Colors.orange:
                isResultHistory(data, resources)? Colors.green: undefined
        }/>
        <div className={styles.header}>
            <UserPanel user={userDescription.set(processDate(data.created_at))(resources.user[data.user] ?? EMPTY_USER)}/>
        </div>
        {
            data.actions.map(historyRenderer(resources, data, true))
        }
        {children}
    </div>
}

export default memo(HistoryElement) as typeof HistoryElement